import { SectionController } from '@sas-dvr/ltjs-bird';
import { from, merge } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { SectionState } from '@sas-dvr/internal-va-react-core/store/section/SectionState';
import { getPagePromptHeaderId } from '@sas-dvr/internal-va-react-core/components/PromptHeader/utils';
import { BIRDModelControllerLoader } from './LoaderFactory';
import { getMediaContainerController } from '../util/controllerUtils';
import { loadHeaderController } from './loadPromptHeader';
import { loadAutoInteractionController } from './loadAutoInteractions';
import { EpicActions } from '../actions/epicActions';
import { loadMediaContainerController } from './loadMediaContainer';
import { withRetained } from '../util';

export const loadSectionController: BIRDModelControllerLoader = (
  controller,
  unload$,
  myActions$,
  state$,
  deps
) => {
  if (!(controller instanceof SectionController)) {
    throw new Error('Expected the controller to be a SectionController');
  }

  const mediaContainerController = getMediaContainerController(controller);
  if (!mediaContainerController) {
    throw new Error('Expected the section to have a MediaContainer');
  }

  const reportController = controller.getReportController();
  if (!reportController) {
    throw new Error('Expected the section to have a ReportController');
  }

  const section = controller.getModel();
  if (!section) {
    throw new Error('Expected the SectionController to have a Section');
  }

  const name = section.getName();
  const setCurrentSectionActions =
    !name || reportController.isHiddenSection(name)
      ? []
      : [
          SectionState.actions.updateSection(name, {
            rootContainerId: deps.getKey(mediaContainerController),
          }),
        ];

  //
  // Update section data, load the media container, and set media container as a layout root.
  //
  const initialLoadActions$ = from([
    ...setCurrentSectionActions,
    EpicActions.actions.addLayoutRoot(mediaContainerController),
  ]);

  //
  // Remove the media container as a layout root and unload it when the section is unloaded.
  //
  const handleUnload$ = unload$.pipe(
    mergeMap(() => from([EpicActions.actions.removeLayoutRoot(mediaContainerController)]))
  );

  return merge(
    initialLoadActions$,
    handleUnload$,
    loadHeaderController(
      controller.getHeaderController(),
      unload$,
      getPagePromptHeaderId(deps.getKey(mediaContainerController)),
      state$
    ),
    loadAutoInteractionController(
      controller.getAutoInteractionController(),
      section,
      myActions$,
      unload$,
      deps.getKey(mediaContainerController)
    ),
    // Load media container controller directly instead of using loadBirdModel.
    // This allows loadAutoInteractionController, loadMediaContainerController,
    // and the UI for each to use the same key.
    // loadBirdModelEpic has special handling for SectionController to allow this case
    loadMediaContainerController(mediaContainerController, unload$, myActions$, state$, deps).pipe(
      withRetained(mediaContainerController)
    )
  );
};
